import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { SearchBar } from '@/components/SearchBar';
import { DogSchoolCard } from '@/components/DogSchoolCard';
import { Button } from '@/components/ui/button';
import { DogSchool } from '@/data/dogSchools';
import { countries } from '@/data/regions';
import { motion } from 'framer-motion';
import { fetchPaginatedDogSchools, fetchDogSchoolsByPostalCode } from '@/services/dogSchoolService';
import { CountryRegionsLinks } from '@/components/CountryRegionsLinks';
import { SortingOptions, SortOption, SortDirection } from '@/components/SortingOptions';

const Index = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [schools, setSchools] = useState<DogSchool[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchApplied, setSearchApplied] = useState(false);
  const [sortOption, setSortOption] = useState<SortOption>('reviewCount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  const loadInitialDogSchools = useCallback(async () => {
    setLoading(true);
    try {
      console.log('Loading initial batch of schools');
      const {
        schools: fetchedSchools,
        hasMore: moreAvailable,
        totalCount
      } = await fetchPaginatedDogSchools(0, 50, {
        country: "Germany"
      });
      console.log(`Loaded initial batch of ${fetchedSchools.length} schools, total: ${totalCount}`);
      setSchools(fetchedSchools);
      setHasMore(moreAvailable);
      setPage(0);
    } catch (error) {
      console.error('Error loading dog schools:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  const loadMoreDogSchools = useCallback(async () => {
    if (!hasMore || isLoadingMore || searchApplied) return;
    
    setIsLoadingMore(true);
    const nextPage = page + 1;
    console.log(`Loading batch ${nextPage} of schools`);
    
    try {
      const {
        schools: newSchools,
        hasMore: moreAvailable,
        totalCount
      } = await fetchPaginatedDogSchools(nextPage, 50, {
        country: "Germany"
      });
      
      console.log(`Loaded batch ${nextPage} with ${newSchools.length} schools, total: ${totalCount}`);
      
      if (newSchools.length > 0) {
        setSchools(prevSchools => [...prevSchools, ...newSchools]);
        setPage(nextPage);
        setHasMore(moreAvailable);
      } else {
        setHasMore(false);
        console.log("No more schools available to load");
      }
    } catch (error) {
      console.error('Error loading more dog schools:', error);
      setHasMore(false); // Prevent further loading attempts on error
    } finally {
      setIsLoadingMore(false);
    }
  }, [page, hasMore, isLoadingMore, searchApplied]);
  
  const handleSearch = async (postalCode: string, city: string, radius: number) => {
    setLoading(true);
    try {
      console.log(`Searching for schools with postal code: ${postalCode}, city: ${city}, radius: ${radius}km`);
      const searchedSchools = await fetchDogSchoolsByPostalCode(postalCode, city, radius);
      
      if (searchedSchools.length === 0) {
        console.log('No schools found for the search criteria');
      } else {
        console.log(`Found ${searchedSchools.length} schools for the search criteria`);
      }
      
      setSchools(searchedSchools);
      setSearchApplied(true);
      setHasMore(false); // Disable infinite scrolling when search is applied
      setPage(0); // Reset page when search is applied
    } catch (error) {
      console.error('Error searching dog schools:', error);
      setSchools([]); // Clear schools on error
    } finally {
      setLoading(false);
    }
  };
  
  const handleSortChange = (option: SortOption, direction: SortDirection) => {
    console.log(`Sort changed to ${option} ${direction}`);
    setSortOption(option);
    setSortDirection(direction);

    // Sorting is applied in the DogSchoolsList component
    // This just updates the state to re-render
  };
  
  const navigateToCountry = (country: string) => {
    navigate(`/${country}`);
  };

  // Setup intersection observer for infinite scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !isLoadingMore && !searchApplied) {
        console.log('Intersection observer triggered, loading more schools');
        loadMoreDogSchools();
      }
    }, {
      threshold: 0.1,
      rootMargin: '200px' // Start loading earlier
    });
    
    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }
    
    observerRef.current = observer;
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, isLoadingMore, loadMoreDogSchools, searchApplied]);

  // Load initial dog schools on mount
  useEffect(() => {
    loadInitialDogSchools();
  }, [loadInitialDogSchools]);
  
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const item = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0
    }
  };

  // Filter out Switzerland from the countries list
  const filteredCountries = countries.filter(country => country.value !== 'switzerland');
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12">
          <span className="px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full inline-block mb-4">
            {t('allDogSchools')}
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
            {t('mainHeading')}
          </h1>
          <div className="flex justify-center mb-4">
            <img src="/lovable-uploads/5dcadb59-e79b-45de-851e-4a6add76a6cc.png" alt="Hundeschulenfinder Logo" className="h-24 w-24 object-contain" />
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('subHeading')}
          </p>
        </motion.section>

        <motion.div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 gap-6 mb-10" variants={container} initial="hidden" animate="show">
          {/* Only show Germany and Austria buttons */}
          <motion.div variants={item}>
            <Button onClick={() => navigateToCountry('germany')} className="w-full h-20 text-lg font-medium bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 rounded-lg shadow-sm transition-all hover:shadow-md">
              {t('germanButton')}
            </Button>
          </motion.div>
          <motion.div variants={item}>
            <Button onClick={() => navigateToCountry('austria')} className="w-full h-20 text-lg font-medium bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 rounded-lg shadow-sm transition-all hover:shadow-md">
              {t('austriaButton')}
            </Button>
          </motion.div>
        </motion.div>

        {/* New Description Section */}
        <motion.section initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="p-6 rounded-lg shadow-sm mb-12 text-gray-700 leading-relaxed bg-slate-100">
          <p className="text-justify font-normal text-lg">Du suchst die beste Hundeschule in deiner Nähe oder den besten, erfahrenen Hundetrainer in deiner Nähe? Mit Hundeschulenfinder.de findest du die besten Hundeschulen und Hundetrainer für Welpen, Junghunde und erwachsene Hunde. Vergleiche Angebote, lies Bewertungen und wähle den besten Trainer für dein Hundetraining. Starte jetzt deine Suche und finde die perfekte Hundeschule in deiner Umgebung.</p>
        </motion.section>

        <section className="mb-12">
          <SearchBar onSearch={handleSearch} isSearching={loading} />
        </section>

        {/* Add sorting options */}
        <section className="mb-6">
          <SortingOptions onSortChange={handleSortChange} defaultOption={sortOption} defaultDirection={sortDirection} />
        </section>

        <section className="mb-16">
          <motion.div className="grid grid-cols-1 gap-8" variants={container} initial="hidden" animate="show">
            {loading && schools.length === 0 ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center p-10 bg-white rounded-lg shadow-sm">
                <p className="text-gray-500">{t('loading')}</p>
              </motion.div>
            ) : schools.length > 0 ? (
              <>
                {schools.map(school => (
                  <motion.div key={school.id} variants={item}>
                    <DogSchoolCard school={school} />
                  </motion.div>
                ))}
                
                {/* Infinite scroll loader */}
                {hasMore && !searchApplied && (
                  <div ref={loaderRef} className="text-center p-6">
                    <p className="text-gray-500">
                      {isLoadingMore ? t('loading') : ''}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center p-10 bg-white rounded-lg shadow-sm">
                <p className="text-gray-500">{t('noSchoolsFound')}</p>
              </motion.div>
            )}
          </motion.div>
        </section>

        {/* All States/Regions by Country - use the CountryRegionsLinks component */}
        <section className="mb-16">
          <CountryRegionsLinks />
        </section>

        {/* Educational Section */}
        <section className="mb-16">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-bold mb-6 text-center text-gray-900">
              {language === 'de' ? 'Die richtige Hundeschule finden' : 'Finding the Right Dog School'}
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-2 text-gray-800">
                  {language === 'de' ? 'Warum Hundetraining wichtig ist' : 'Why Dog Training Matters'}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {language === 'de' ? 'Ein gut trainierter Hund ist ein glücklicher Hund. Professionelles Training fördert nicht nur Gehorsam, sondern stärkt auch die Bindung zwischen Ihnen und Ihrem Vierbeiner. Es hilft, Verhaltensprobleme zu vermeiden und sorgt für ein harmonisches Zusammenleben.' : 'A well-trained dog is a happy dog. Professional training not only promotes obedience but also strengthens the bond between you and your four-legged friend. It helps prevent behavioral problems and ensures harmonious coexistence.'}
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2 text-gray-800">
                  {language === 'de' ? 'Worauf Sie bei der Auswahl einer Hundeschule achten sollten' : 'What to Look For When Choosing a Dog School'}
                </h3>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li>
                    {language === 'de' ? 'Qualifikationen der Trainer: Achten Sie auf professionelle Zertifizierungen und Erfahrung.' : 'Trainer qualifications: Look for professional certifications and experience.'}
                  </li>
                  <li>
                    {language === 'de' ? 'Trainingsmethoden: Moderne Hundeschulen setzen auf positive Verstärkung statt auf Bestrafung.' : 'Training methods: Modern dog schools focus on positive reinforcement rather than punishment.'}
                  </li>
                  <li>
                    {language === 'de' ? 'Gruppengröße: Kleinere Gruppen ermöglichen eine individuellere Betreuung.' : 'Group size: Smaller groups allow for more individualized attention.'}
                  </li>
                  <li>
                    {language === 'de' ? 'Kursangebot: Verschiedene Kurse für unterschiedliche Alters- und Erfahrungsstufen.' : 'Course offerings: Various courses for different age and experience levels.'}
                  </li>
                  <li>
                    {language === 'de' ? 'Einrichtungen: Saubere, sichere Trainingsumgebung mit ausreichend Platz.' : 'Facilities: Clean, safe training environment with adequate space.'}
                  </li>
                  <li>
                    {language === 'de' ? 'Bewertungen: Erfahrungsberichte anderer Hundehalter können wertvolle Einblicke geben.' : 'Reviews: Testimonials from other dog owners can provide valuable insights.'}
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2 text-gray-800">
                  {language === 'de' ? 'Der erste Besuch' : 'The First Visit'}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {language === 'de' ? 'Viele Hundeschulen bieten kostenlose Probestunden an. Nutzen Sie diese Gelegenheit, um die Atmosphäre zu erleben und zu sehen, wie Ihr Hund auf die Umgebung und den Trainer reagiert. Eine gute Hundeschule sollte Ihnen und Ihrem Hund ein angenehmes Gefühl vermitteln.' : 'Many dog schools offer free trial lessons. Take advantage of this opportunity to experience the atmosphere and see how your dog responds to the environment and the trainer. A good dog school should make both you and your dog feel comfortable.'}
                </p>
              </div>
            </div>
          </motion.div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
